// overview/FlatView — kompakt list-vy per grupp. Extraherad ur
// OverviewDialog (#6).

import { useMemo } from 'react';
import type { WorkflowBlock, Connector, BlockGroup } from '@/types/workflow';
import { useGroupData } from './useGroupData';
import { OverviewBlock, Arrow, CrossGroupConnectors } from './shared';

function FlatGroupSection({
  group,
  blocks,
  connectors,
  children,
}: {
  group: BlockGroup;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  children?: React.ReactNode;
}) {
  const { groupBlocks, ordered, connectedPairs } = useGroupData(group, blocks, connectors);

  return (
    <div
      className="rounded-xl border-2 p-4"
      style={{
        borderColor: group.color,
        backgroundColor: `${group.color}08`,
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-3 h-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: group.color }}
        />
        <span className="text-sm font-semibold" style={{ color: group.color }}>
          {group.name || 'Group'}
        </span>
        <span className="text-xs text-muted-foreground ml-1">
          ({groupBlocks.length} blocks)
        </span>
      </div>

      {ordered.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          {ordered.map((id, i) => {
            const block = groupBlocks.find(b => b.id === id);
            if (!block) return null;
            const prevId = i > 0 ? ordered[i - 1] : null;
            const showArrow = prevId && connectedPairs.has(`${prevId}->${id}`);
            return (
              <div key={id} className="flex items-center gap-1">
                {showArrow && <Arrow />}
                <OverviewBlock block={block} />
              </div>
            );
          })}
        </div>
      )}

      {children}
    </div>
  );
}

export function FlatView({
  blocks,
  connectors,
  groups,
  crossGroupConnectors,
  resolveName,
  resolveColor,
}: {
  blocks: WorkflowBlock[];
  connectors: Connector[];
  groups: BlockGroup[];
  crossGroupConnectors: Connector[];
  resolveName: (id: string) => string;
  resolveColor: (id: string) => string;
}) {
  const groupIds = useMemo(() => new Set(groups.map(g => g.id)), [groups]);
  const blockConnectors = connectors.filter(c => !groupIds.has(c.fromId) && !groupIds.has(c.toId));
  const topLevelGroups = groups.filter(g => !g.parentGroupId);
  const childGroupsOf = (parentId: string) => groups.filter(g => g.parentGroupId === parentId);
  const allGroupedBlockIds = new Set(groups.flatMap(g => g.blockIds));
  const ungroupedBlocks = blocks.filter(b => !allGroupedBlockIds.has(b.id));

  function renderGroupTree(group: BlockGroup, depth: number): React.ReactNode {
    const children = childGroupsOf(group.id);
    return (
      <div key={group.id} style={{ marginLeft: depth > 0 ? 16 : 0 }}>
        <FlatGroupSection group={group} blocks={blocks} connectors={blockConnectors}>
          {children.length > 0 && (
            <div className="mt-3 space-y-2">
              {children.map(child => renderGroupTree(child, depth + 1))}
            </div>
          )}
        </FlatGroupSection>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {topLevelGroups.map(g => renderGroupTree(g, 0))}

      {ungroupedBlocks.length > 0 && (
        <div className="rounded-xl border-2 border-dashed border-muted-foreground/30 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-semibold text-muted-foreground">Root (ungrouped)</span>
            <span className="text-xs text-muted-foreground">({ungroupedBlocks.length} blocks)</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {ungroupedBlocks.map(b => <OverviewBlock key={b.id} block={b} />)}
          </div>
        </div>
      )}

      <CrossGroupConnectors connectors={crossGroupConnectors} resolveName={resolveName} resolveColor={resolveColor} />
    </div>
  );
}

// ─── DETAILED VIEW ──────────────────────────────────────────────────

